// Who or what an RFQ is waiting on. Pure, no database.
//
// The only collaboration state Copilot V1 adds. The app had no owner, assignee
// or waiting-on structure anywhere, so this is new, and deliberately small: one
// append-only record per change, the newest is current. It is NOT a workflow
// engine and nothing advances it automatically. A person sets it.
//
// "Needs Austin" / "Needs Miranda" are `needs_user` with an assignee, labelled
// from the assignee's name, so a second tenant gets "Needs <their person>"
// without a code change.

import { z } from 'zod';

export const ATTENTION_STATES = [
  'needs_user',
  'waiting_customer',
  'waiting_supplier',
  'ready_to_quote',
  'at_risk',
  'done',
] as const;
export type AttentionState = (typeof ATTENTION_STATES)[number];

export const ATTENTION_LABEL: Record<Exclude<AttentionState, 'needs_user'>, string> = {
  waiting_customer: 'Waiting on customer',
  waiting_supplier: 'Waiting on supplier',
  ready_to_quote: 'Ready to quote',
  at_risk: 'At risk',
  done: 'Done',
};

export function firstName(full: string | null | undefined, email?: string | null): string {
  const f = (full ?? '').trim().split(/\s+/)[0];
  if (f) return f;
  const local = (email ?? '').split('@')[0] ?? '';
  return local ? local.charAt(0).toUpperCase() + local.slice(1) : 'someone';
}

export function attentionLabel(state: AttentionState, assignee?: { fullName: string | null; email: string | null } | null): string {
  if (state === 'needs_user') return `Needs ${firstName(assignee?.fullName, assignee?.email)}`;
  return ATTENTION_LABEL[state];
}

/** A structured handoff summary. When a model drafted it, the record says so
 *  and the UI labels it; the human note is always stored separately. */
export const HandoffSummary = z.object({
  goal: z.string().max(600).optional(),
  customerRequirement: z.string().max(800).optional(),
  currentBestOffer: z.string().max(600).optional(),
  suppliersContacted: z.array(z.string().max(300)).max(30).optional(),
  outstanding: z.array(z.string().max(400)).max(20).optional(),
  commitmentsMade: z.array(z.string().max(400)).max(20).optional(),
  doNotPromise: z.array(z.string().max(400)).max(20).optional(),
  nextActions: z.array(z.string().max(400)).max(20).optional(),
  escalateIf: z.array(z.string().max(400)).max(20).optional(),
}).strict();
export type HandoffSummary = z.infer<typeof HandoffSummary>;

export const HandoffInput = z.object({
  attention: z.enum(ATTENTION_STATES),
  /** tenant_members.id, the identifier /api/team/members already exposes.
   *  Checked server-side against the caller's tenant; anyone else is refused. */
  assigneeMembershipId: z.string().uuid().nullable().optional(),
  note: z.string().trim().max(4000).optional(),
  summary: HandoffSummary.optional(),
  /** True when `summary` was drafted by Copilot (labelled as such forever). */
  summaryByCopilot: z.boolean().optional(),
}).superRefine((v, ctx) => {
  if (v.attention === 'needs_user' && !v.assigneeMembershipId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['assigneeMembershipId'], message: 'Choose who this needs.' });
  }
  if (v.summaryByCopilot && !v.summary) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['summary'], message: 'A Copilot summary flag needs a summary.' });
  }
});
export type HandoffInput = z.infer<typeof HandoffInput>;
